<template>
  <div class="container columns is-desktop">
    <div class="column" :class="{'is-one-third-desktop': !!selectedUni}">
      <div class="map-and-header pt-4">
        <div class="content has-text-centered-touch">
          <h1 id="map" v-if="selectedUni" class="map-heading">
              What ties does <span class="university-name">{{ selectedUni.name }}</span> have to Israel?
          </h1>
          <div v-else>
            <h1>
              What ties do {{contact.genericInstitutions}} have to Israel?
            </h1>
            <h5>Select an institution on the map to find out more!</h5>
          </div>
        </div>

        <div v-if="!mapLoaded" class="loading-container">
          <div>Loading map...</div>
        </div>

        <div v-show="mapLoaded" class="map" :class="{'is-hidden-touch': !!selectedUni}">
          <img class="mapImage" :src="require('@/country_specific/assets/map.svg')" @load="mapLoaded=true" />
          <div v-for="academy in academicInstitutions" :key="academy.name">
            <div
                :class="{
                  'dot': true,
                  [academy.category]: true,
                  'selected': selectedUni?.tag === academy.tag,
                }"
                :style="{
                      left: `${pixelPosition(academy).x}%`,
                      top: `${pixelPosition(academy).y}%`,
                }"
                @click="selectUni(academy)">
            </div>
          </div>
        </div> <!-- .map -->

        <div class="has-text-centered" v-if="selectedUni">
          <div class="has-text-weight-bold">Click for other institutions:</div>
          <button class="button m-2" @click.prevent="$emit('select-uni-relative', -1)">Previous</button>
          <button class="button m-2" @click.prevent="$emit('select-uni-relative', 1)">Next</button>
        </div>
      </div> <!-- .map-and-header -->
    </div> <!-- .column -->

    <div class="column" v-if="selectedUni">
      <div class="pt-4">
        <TiesList :academy="selectedUni" />
      </div>
    </div>

  </div> <!-- .columns -->
</template>

<script>
import contact from "@/country_specific/assets/references/countrySpecificValues.json"
import {academicInstitutions} from "@/country_specific/assets/references/academic_institutions"
import TiesList from "@/core/components/TiesList.vue";

export default {
    name: "InteractiveMap",
    components: {
        TiesList,
    },
    emits: ["select-uni", "select-uni-relative"],
    props: ["selectedUni"],
    data() {
        return {
            academicInstitutions,
            contact,
            mapLoaded: false,
        };
    },

    methods: {
        pixelPosition(uni) {
          // France coordinates are from the wikipedia page for the map:
          // https://commons.wikimedia.org/wiki/File:France_location_map-Regions_and_departements-2016.svg?uselang=fr
          // Ouest : 005° 48' W
          // Est : 10° E
          // Nord : 51° 30' N
          // Sud : 41° N
          const topLeft = {lat: 51.5, lon: -5.8};
          const bottomRight = {lat: 41, lon: 10};
          const width = (topLeft.lon - bottomRight.lon);
          const height = (topLeft.lat - bottomRight.lat);

          const x = (topLeft.lon - uni.lon) / width * 100;
          const y = (topLeft.lat - uni.lat) / height * 100;

          const map_dim_x = 2000;
          const map_dim_y = 1922.037;


          // Map idf to a small thing next to the picture
          const rect1 = {
            x: 26.616139999999973 * 100 / map_dim_x,
            y: 18.298580000000015 * 100 / map_dim_y,
            width: 355.15878 * 100 / map_dim_x,
            height: 406.72751 * 100 / map_dim_y
          };

          const rect2 = {
            x: 1001.4313 * 100 / map_dim_x,
            y: 450.81046 * 100 / map_dim_y,
            width: 68.203796 * 100 / map_dim_x,
            height: 71.530807 * 100 / map_dim_y
          };

          const r_x = (x - rect2.x) / rect2.width;
          const r_y = (y - rect2.y) / rect2.height;

          if (
            0 < r_x && r_x < 1 && 0 < r_y && r_y < 1 && r_x + (1-r_y) > 0.5){

            return {
              x: rect1.x + r_x * rect1.width,
              y: rect1.y + r_y * rect1.height
            };
          }

          return { x, y };

        },

        selectUni(academy) {
          if (!academy) {
            return
          }
          this.$emit("select-uni", academy)
        },
    }
};

</script>

<style scoped lang="scss">
@use "/src/country_specific/assets/css/variables";
@use "bulma/sass/utilities/mixins";

.university {
  background-color: var(--bulma-primary);
}

.university-name {
  color: var(--bulma-primary);
}

.research-lab {
  background-color: var(--bulma-primary-light);
}

.mapImage {
    display: block;
    @include mixins.from(variables.$desktop) {
      max-height: calc(75vh);
    }
}

@include mixins.from(variables.$desktop) {
  .column.is-one-third .mapImage {
    width: 100%;
  }
}

.map-heading {
  // Make sure that map does not jump around when the map headline
  // changes between one and two lines
  @include mixins.from(variables.$desktop) {
    min-height: 72px;
  }
}

.map {
    position: relative;
    margin-left: auto;
    margin-right: auto;
    width: fit-content;
    padding: 0;
    flex: content;
}

.map-and-header {
  position: sticky;
  top: 10px;
}

.dot {
  position: absolute;
  width: min(10%, 10px);
  aspect-ratio: 1 / 1;
  border-radius: 100%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 2;
  border: 1px solid black;
}

.dot:hover {
  transform: translate(-50%, -50%) scale(1.2);
  z-index: 10;
}

.dot.selected {
  transform: translate(-50%, -50%) scale(1.2);
  box-shadow: 0 0 0 4px rgba(100,100,100,0.3);
  z-index: 20;
}

.blurb {
  margin: 20px 10px;
  padding: 0;
  align-content: center;
  text-align: center;
}

.description-box p {
  margin: 0;
  font-size: 12px;
  color: #666;
}

</style>
